<template>
  <div class="strategy-detail-page" :class="{ 'is-mobile': isMobileRoute }">
    <div class="strategy-detail-shell">
      <div v-if="!isMobileRoute" class="detail-breadcrumb">
        <span @click="goToStrategyList">攻略</span>
        <span class="breadcrumb-separator">&gt;</span>
        <span>{{ currentCategoryTitle }}</span>
      </div>

      <section class="detail-card">
        <template v-if="isPageLoading">
          <div class="detail-loading-block detail-loading-title"></div>
          <div class="detail-loading-meta-row">
            <div class="detail-loading-block detail-loading-meta"></div>
            <div class="detail-loading-block detail-loading-meta short"></div>
          </div>
          <div class="detail-loading-block detail-loading-content"></div>
        </template>

        <template v-else>
          <h1 class="detail-title">{{ strategyData?.title }}</h1>

        <div v-if="isMobileRoute" class="mobile-detail-author">
          <span class="mobile-author-avatar">{{ getAuthorInitial(strategyData) }}</span>
          <div>
            <div class="mobile-author-name">{{ strategyData?.nickName || '化化平台官方' }}</div>
            <div class="mobile-publish-date">{{ strategyData?.createTime }}</div>
          </div>
        </div>

        <div v-else class="detail-meta">
          <span>发布时间：{{ strategyData?.createTime }}</span>
          <span class="meta-divider"></span>
          <span>发布人：{{ strategyData?.nickName }}</span>
        </div>

        <div class="detail-content-placeholder">
          <div class="placeholder-tip" v-html="strategyData?.content"></div>
        </div>

        <section v-if="!isMobileRoute" class="related-section">
          <h2 class="related-title">看看其他攻略</h2>

          <div class="related-grid">
            <article
              v-for="article in relatedArticles"
              :key="article.id"
              class="related-card"
              @click="goToRelatedDetail(article)"
            >
              <div class="related-card-cover">
                <img :src="article?.coverImg?.url || fallbackCover" :alt="article.title" @error="handleRelatedCoverError($event, article)">
              </div>
              <h3 class="related-card-title">{{ article.title }}</h3>
            </article>
          </div>
        </section>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { getStrategyDetail, getStrategyPageList } from '@/api/strategy'
import {
  buildStrategyDetailSeoHead,
  getStrategyArticleRouteMeta,
  getStrategyCategoryChildIds,
  getStrategyCategoryTitle,
  getStrategyDetailSeoHead
} from '@/utils/seo-route-data'
import { getPageSsgState, setPageSsgState } from '@/utils/ssg-state'

const SSG_STATE_KEY = 'strategyDetailPage'

function getCanonicalUrl(strategyId) {
  return strategyId
    ? `https://www.huahuaeco.com/strategy/detail/${strategyId}`
    : 'https://www.huahuaeco.com/strategy/detail'
}

export default {
  name: 'StrategyDetailPage',
  setup() {
    const route = useRoute()
    const strategyId = route.params.id || route.query.id
    const seoHead = ref(getStrategyDetailSeoHead(strategyId))
    const canonicalUrl = ref(getCanonicalUrl(strategyId))

    useHead(() => ({
      title: seoHead.value.title,
      meta: [
        { name: 'description', content: seoHead.value.description },
        { name: 'keywords', content: seoHead.value.keywords }
      ],
      link: [
        { rel: 'canonical', href: canonicalUrl.value }
      ]
    }))

    return {
      canonicalUrl,
      seoHead
    }
  },
  data() {
    return {
      detailTitle: '装修全流程指南：从筹备到入住，一步不踩空',
      publishDate: '2026-05-28',
      author: '化化平台攻略',
      categoryTitleMap: {
        newbie: '新手指南',
        construction: '硬装施工',
        styling: '软装搭配',
        materials: '选材指南',
        faq: '常见问题'
      },
      relatedArticles: [],
      fallbackCover: 'https://obs.huahuaeco.com/official/product2.png',

      strategyData: null,
      categoryId: null,
      childCategoryId: null,
      strategyId: null,
      strategyCategories: [],
      isPageLoading: typeof window !== 'undefined',
      currentCategoryTitle: ''
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.refreshRouteData()
      },
      deep: true
    },
    '$route.params': {
      handler() {
        this.refreshRouteData()
      },
      deep: true
    }
  },
  computed: {
    isMobileRoute() {
      return this.$route.path === '/m/strategy/detail' || this.$route.path.startsWith('/m/strategy/detail/')
    },
    currentCategoryKey() {
      return this.categoryTitleMap[this.categoryId] ? this.categoryId : ''
    },
  },
  created() {
    this.applySsgState()
  },
  mounted() {
    this.refreshRouteData()
  },
  serverPrefetch() {
    return this.syncRouteData()
  },
  methods: {
    applySsgState() {
      const state = getPageSsgState(this.$route, SSG_STATE_KEY)
      if (!state) {
        return
      }

      this.strategyData = state.strategyData || null
      this.relatedArticles = Array.isArray(state.relatedArticles) ? state.relatedArticles : []
      this.categoryId = state.categoryId || null
      this.childCategoryId = state.childCategoryId || null
      this.strategyId = state.strategyId || null
      this.strategyCategories = Array.isArray(state.strategyCategories) ? state.strategyCategories : []
      this.currentCategoryTitle = state.currentCategoryTitle || ''
      if (state.canonicalUrl) {
        this.canonicalUrl = state.canonicalUrl
      }
      if (state.seoHead) {
        this.seoHead = state.seoHead
      }
    },
    persistSsgState() {
      setPageSsgState(this.$route, SSG_STATE_KEY, {
        strategyData: this.strategyData,
        relatedArticles: this.relatedArticles,
        categoryId: this.categoryId,
        childCategoryId: this.childCategoryId,
        strategyId: this.strategyId,
        strategyCategories: this.strategyCategories,
        currentCategoryTitle: this.currentCategoryTitle,
        canonicalUrl: this.canonicalUrl,
        seoHead: this.seoHead
      })
    },
    withMobilePrefix(path) {
      return this.isMobileRoute ? `/m${path}` : path
    },
    goToStrategyList() {
      const path = this.categoryId ? `/strategy/list/${this.categoryId}` : '/strategy/list'
      this.$router.push({ path: this.withMobilePrefix(path) })
    },
    async syncRouteData() {
      const { categoryId, childCategoryId, id } = this.$route.query
      this.categoryId = categoryId || ''
      this.strategyId = this.$route.params.id || id || ''
      this.childCategoryId = childCategoryId || ''
      if (this.strategyId) {
        this.canonicalUrl = getCanonicalUrl(this.strategyId)
        await this.fetchStrategyDetail(this.strategyId)
        const cachedRouteMeta = getStrategyArticleRouteMeta(this.strategyId)
        this.categoryId = this.categoryId
          || this.strategyData?.parentCategoryId
          || this.strategyData?.categoryParentId
          || cachedRouteMeta.categoryId
          || ''
        this.childCategoryId = this.childCategoryId
          || this.strategyData?.childCategoryId
          || this.strategyData?.strategyCategoryId
          || this.strategyData?.categoryId
          || cachedRouteMeta.childCategoryId
          || ''
        await this.getStrategyPageListApi()
        if (typeof localStorage !== 'undefined') {
          this.strategyCategories = JSON.parse(localStorage.getItem('strategyCategories') || '[]')
        }
        this.currentCategoryTitle = this.strategyCategories.find((cat) => String(cat.id) === String(this.categoryId))?.name
          || getStrategyCategoryTitle(this.categoryId)
          || ''
        this.seoHead = buildStrategyDetailSeoHead(this.strategyData)
        this.persistSsgState()
      }
    },
    async refreshRouteData() {
      this.isPageLoading = true
      try {
        await this.syncRouteData()
      } finally {
        this.isPageLoading = false
      }
    },
    async fetchStrategyDetail(strategyId) {
      try {
        const res = await getStrategyDetail(strategyId)
        this.strategyData = res.data
      } catch (error) {
        console.error('Failed to fetch strategy detail:', error)
      }
    },
    goToRelatedDetail(article) {
      this.$router.push({
        path: this.withMobilePrefix('/strategy/detail'),
        query: {
          categoryId: this.categoryId,
          childCategoryId: this.childCategoryId,
          id: article.id
        }
      })
    },
    // 通过getStrategyPageList获取当前类别下的攻略；SSG保持稳定输出，客户端保留随机展示
    async getStrategyPageListApi() {
      const childCategoryIds = this.childCategoryId
        ? [this.childCategoryId]
        : getStrategyCategoryChildIds(this.categoryId)
      const categoryIdList = [this.categoryId, ...childCategoryIds].filter(Boolean)
      const params = {
        pageNum: 1,
        pageSize: 50
      }
      if (categoryIdList.length) {
        params.categoryIdList = categoryIdList
      }

      try {
        const res = await getStrategyPageList(params)
        const strategyList = res?.data?.data || res?.data?.records || []
        const relatedList = (Array.isArray(strategyList) ? strategyList : [])
          .filter((article) => String(article.id) !== String(this.strategyId))
        if (typeof window !== 'undefined') {
          relatedList.sort(() => Math.random() - 0.5)
        }
        this.relatedArticles = relatedList.slice(0, 6)
      } catch (error) {
        console.error('Failed to fetch related strategies:', error)
      }
    },
    getAuthorInitial(article) {
      const name = article?.nickName || '化'
      return String(name).trim().slice(0, 1) || '化'
    },
    handleRelatedCoverError(event, article) {
      const target = event?.target
      if (target && target.getAttribute('src') !== this.fallbackCover) {
        if (article) {
          article.coverImg = {
            ...(article.coverImg || {}),
            url: this.fallbackCover
          }
        }
        target.src = this.fallbackCover
        return
      }

      if (target) {
        target.style.visibility = 'hidden'
      }
    }
  }
}
</script>

<style scoped lang="scss">
.strategy-detail-page {
  padding: 92px 0 80px;
  min-height: 100vh;
  background: #f3f4f7;
}

.strategy-detail-shell {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.detail-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  color: #7d7d7d;
  font-size: 12px;
  line-height: 1.4;
}

.detail-breadcrumb span:nth-child(1) {
  cursor: pointer;
}

.breadcrumb-separator {
  color: #9a9a9a;
}

.detail-card {
  padding: 22px 26px 34px;
  background: #fff;
}

.detail-loading-block {
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.55) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: detail-loading-shimmer 1.2s ease-in-out infinite;
}

.detail-loading-title {
  width: min(680px, 76%);
  height: 32px;
}

.detail-loading-meta-row {
  display: flex;
  gap: 18px;
  margin-top: 20px;
}

.detail-loading-meta {
  width: 180px;
  height: 14px;
}

.detail-loading-meta.short {
  width: 130px;
}

.detail-loading-content {
  min-height: 620px;
  margin-top: 24px;
}

.detail-title {
  margin: 0;
  color: #333;
  font-size: 21px;
  line-height: 1.5;
  font-weight: 600;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 18px;
  color: #999;
  font-size: 12px;
  line-height: 1.4;
}

.meta-divider {
  width: 1px;
  height: 12px;
  background: #d9d9d9;
}

.detail-content-placeholder {
  position: relative;
  margin-top: 22px;
  min-height: 620px;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
}

.placeholder-tip {
  top: 16px;
  left: 16px;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.88);
  color: #999;
  font-size: 12px;
  line-height: 1.4;
}

.related-section {
  margin-top: 24px;
}

.related-title {
  margin: 0 0 18px;
  color: #333;
  font-size: 28px;
  line-height: 1.4;
  font-weight: 600;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 26px 18px;
}

.related-card {
  min-width: 0;
  cursor: pointer;
}

.related-card-cover {
  overflow: hidden;
  border-radius: 4px;
  background: #f5f1eb;
}

.related-card-cover img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 0.78;
  object-fit: cover;
  border-radius: 10px;
}

.related-card-title {
  margin: 8px 0 0;
  color: #222;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 500;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes detail-loading-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.strategy-detail-page.is-mobile {
  min-height: 100vh;
  padding: calc(88 * var(--rpx)) 0 calc(48 * var(--rpx));
  background: #fff;

  .strategy-detail-shell {
    width: 100%;
    margin: 0;
  }

  .detail-card {
    padding: calc(28 * var(--rpx)) calc(24 * var(--rpx)) calc(44 * var(--rpx));
    background: #fff;
  }

  .detail-loading-title {
    width: 84%;
    height: calc(44 * var(--rpx));
  }

  .detail-loading-meta-row {
    gap: calc(14 * var(--rpx));
    margin-top: calc(24 * var(--rpx));
  }

  .detail-loading-meta {
    width: calc(190 * var(--rpx));
    height: calc(22 * var(--rpx));
  }

  .detail-loading-content {
    min-height: calc(760 * var(--rpx));
    margin-top: calc(30 * var(--rpx));
  }

  .detail-title {
    color: #262626;
    font-size: calc(34 * var(--rpx));
    font-weight: 700;
    line-height: 1.48;
  }

  .mobile-detail-author {
    display: flex;
    align-items: center;
    gap: calc(14 * var(--rpx));
    margin-top: calc(22 * var(--rpx));
  }

  .mobile-author-avatar {
    width: calc(54 * var(--rpx));
    height: calc(54 * var(--rpx));
    border-radius: 50%;
    color: #fff;
    text-align: center;
    font-size: calc(24 * var(--rpx));
    font-weight: 700;
    line-height: calc(54 * var(--rpx));
    background: linear-gradient(135deg, #5bf7fe, #198cfe);
  }

  .mobile-author-name {
    color: #262626;
    font-size: calc(24 * var(--rpx));
    font-weight: 600;
    line-height: 1.35;
  }

  .mobile-publish-date {
    margin-top: calc(4 * var(--rpx));
    color: #8c8c8c;
    font-size: calc(20 * var(--rpx));
    line-height: 1.35;
  }

  .detail-content-placeholder {
    margin-top: calc(30 * var(--rpx));
    min-height: calc(760 * var(--rpx));
    background: #fff;
  }

  .placeholder-tip {
    padding: 0;
    border-radius: 0;
    background: transparent;
    color: #262626;
    font-size: calc(26 * var(--rpx));
    line-height: 1.82;
  }

  .placeholder-tip :deep(img) {
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    margin: calc(22 * var(--rpx)) auto;
    border-radius: calc(4 * var(--rpx));
  }

  .placeholder-tip :deep(p) {
    margin: 0 0 calc(24 * var(--rpx));
  }
}

@media (max-width: 960px) {
  .strategy-detail-page {
    padding: 84px 0 56px;
  }

  .strategy-detail-shell {
    width: min(100%, calc(100% - 32px));
  }

  .detail-card {
    padding: 18px 18px 28px;
  }

  .detail-content-placeholder {
    min-height: 520px;
  }

  .related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .detail-title {
    font-size: 18px;
  }

  .detail-meta {
    flex-wrap: wrap;
    gap: 10px;
  }

  .related-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
</style>
